import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
  Tooltip,
  Card,
  Grid,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Menu,
} from '@mui/material';
import {
  Delete,
  CheckCircle,
  Schedule,
  Error as ErrorIcon,
  ThumbUp,
  CloudUpload,
  Add,
  DeleteSweep,
  Recycling,
  Close,
  FileDownload,
  Edit,
  Visibility,
  Warning,
  AttachFile,
  Business,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useDropzone } from 'react-dropzone';
import { usePermissions } from '../../hooks/usePermissions';
import { ApprovalDialog } from '../shared/ApprovalDialog';
import { useApp } from '../../contexts/AuthContext';
import { wasteService, WasteEntry, WasteSummary } from '../../services';

interface WasteData {
  id: string;
  category: string;
  wasteType: string;
  disposalMethod: string;
  amount: number;
  unit: string;
  facility: string;
  facility_id: string;
  period: string;
  hazardous: boolean;
  status: string;
  uploadedBy?: string;
  uploadedAt?: string;
  contractor?: string;
  wasteManifestId?: string;
  attachmentCount?: number;
}

const WASTE_CATEGORIES = [
  { value: 'Non-Hazardous', label: 'Non-Hazardous Waste' },
  { value: 'Hazardous', label: 'Hazardous Waste' },
];

// Waste types aligned with backend WasteType enum (GRI 306)
const WASTE_TYPES = {
  'Non-Hazardous': ['General Waste', 'Paper/Cardboard', 'Plastics', 'Glass', 'Metal', 'Organic/Food', 'Textile/Fabric', 'Construction Debris', 'Other'],
  'Hazardous': ['E-waste', 'Chemical Waste', 'Medical Waste', 'Other'],
};

// Disposal methods aligned with backend DisposalMethod enum (GRI 306)
const DISPOSAL_METHODS = {
  'Non-Hazardous': ['Landfill', 'Recycled', 'Composting', 'Incineration with Energy Recovery', 'Reuse', 'Other'],
  'Hazardous': ['Specialized Treatment', 'Incineration', 'Deep Well Injection', 'Other'],
};

export const WasteDataCollection: React.FC = () => {
  const { user, selectedPeriod, facilities } = useApp();
  const permissions = usePermissions();
  const [wasteData, setWasteData] = useState<WasteData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<WasteData | null>(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Non-Hazardous, 1: Hazardous
  const [editMode, setEditMode] = useState(false);
  const [editingEntry, setEditingEntry] = useState<WasteData | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter state
  const [facilityFilter, setFacilityFilter] = useState<string>('all');

  // Summary state
  const [summary, setSummary] = useState<WasteSummary | null>(null);

  const [formData, setFormData] = useState({
    category: '',
    wasteType: '',
    disposalMethod: '',
    amount: '',
    unit: 'kg',
    facility_id: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    contractor: '',
    wasteManifestId: '',
    notes: '',
  });

  // Evidence files state
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);

  // Facility filter options
  const facilityOptions = [
    { value: 'all', label: 'All Facilities' },
    ...facilities.map(f => ({ value: f.id, label: f.name }))
  ];

  // Fetch data from API
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entriesResponse, summaryResponse] = await Promise.all([
        wasteService.listEntries({ period: selectedPeriod?.year?.toString() }),
        wasteService.getSummary(selectedPeriod?.year?.toString()),
      ]);

      // Transform API response to component format
      const transformedData: WasteData[] = (entriesResponse.entries || []).map((entry: WasteEntry) => ({
        id: entry.id,
        category: entry.category,
        wasteType: entry.waste_type,
        disposalMethod: entry.disposal_method,
        amount: entry.amount,
        unit: entry.unit,
        facility: entry.facility_name,
        facility_id: entry.facility_id,
        period: entry.reporting_period,
        hazardous: entry.category === 'Hazardous',
        status: entry.status,
        uploadedBy: entry.created_by,
        uploadedAt: entry.created_at,
        contractor: (entry as any).contractor,
        wasteManifestId: (entry as any).waste_manifest_id,
        attachmentCount: (entry as any).attachment_count || 0,
      }));

      setWasteData(transformedData);
      setSummary(summaryResponse);
    } catch (err: any) {
      setError(err.message || 'Failed to load waste data');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod?.year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when switching tabs
  useEffect(() => {
    setPage(0);
    setFacilityFilter('all');
  }, [activeTab]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    onDrop: (files) => {
      console.log('Files uploaded:', files);
      setUploadDialogOpen(false);
    },
  });

  // Get category for current tab
  const getCategoryForTab = (tab: number): string => {
    return tab === 0 ? 'Non-Hazardous' : 'Hazardous';
  };

  // Filter data by tab and facility
  const getFilteredData = (): WasteData[] => {
    let data = wasteData;

    // Filter by tab (category)
    const category = getCategoryForTab(activeTab);
    data = data.filter(d => d.category === category);

    // Filter by facility
    if (facilityFilter !== 'all') {
      data = data.filter(d => d.facility_id === facilityFilter);
    }

    // Filter by permissions
    if (permissions.userRole === 'Contributor') {
      data = data.filter(item => item.uploadedBy === user?.name || item.status === 'approved');
    } else if (permissions.userRole !== 'Admin') {
      data = data.filter(item => item.status === 'approved');
    }

    return data;
  };

  const filteredData = getFilteredData();

  // Pagination
  const getCurrentPageData = (data: WasteData[]) => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  // Calculate summary stats
  const totalWaste = summary?.total_waste || 0;
  const totalHazardous = summary?.total_hazardous || 0;
  const totalNonHazardous = summary?.total_non_hazardous || 0;
  const recyclingRate = summary?.recycling_rate || 0;
  const diversionRate = summary?.diversion_rate || 0;
  const approvedCount = summary?.approved_count || 0;
  const pendingCount = summary?.pending_count || 0;
  const totalCount = wasteData.length;

  // Chart data - aggregate by month (only approved entries)
  const getChartData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const chartData = months.map((month, idx) => {
      // Only include approved entries in chart
      const monthData = wasteData.filter(d => {
        const [, m] = d.period?.split('-') || [];
        return parseInt(m) === idx + 1 && d.status === 'approved';
      });

      const nonHazardous = monthData.filter(d => d.category === 'Non-Hazardous').reduce((sum, d) => sum + d.amount, 0);
      const hazardous = monthData.filter(d => d.category === 'Hazardous').reduce((sum, d) => sum + d.amount, 0);
      const recycled = monthData.filter(d => ['Recycled', 'Composting', 'Reuse'].includes(d.disposalMethod)).reduce((sum, d) => sum + d.amount, 0);

      return {
        month,
        'Non-Hazardous': nonHazardous,
        'Hazardous': hazardous,
        'Diverted': recycled,
      };
    });
    return chartData;
  };

  const chartData = getChartData();

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { icon: React.ReactElement; color: string; bg: string; label: string }> = {
      approved: { icon: <CheckCircle sx={{ fontSize: 16 }} />, color: '#059669', bg: '#ecfdf5', label: 'Approved' },
      submitted: { icon: <Schedule sx={{ fontSize: 16 }} />, color: '#d97706', bg: '#fffbeb', label: 'Pending' },
      pending: { icon: <Schedule sx={{ fontSize: 16 }} />, color: '#d97706', bg: '#fffbeb', label: 'Pending' },
      draft: { icon: <Edit sx={{ fontSize: 16 }} />, color: '#6b7280', bg: '#f3f4f6', label: 'Draft' },
      rejected: { icon: <ErrorIcon sx={{ fontSize: 16 }} />, color: '#dc2626', bg: '#fef2f2', label: 'Rejected' },
    };
    return configs[status] || configs.draft;
  };

  const handleApprove = async (entryId: string, comment?: string) => {
    try {
      await wasteService.approveEntry(entryId, comment);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to approve entry');
    }
  };

  const handleReject = async (entryId: string, reason: string) => {
    try {
      await wasteService.rejectEntry(entryId, reason);
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to reject entry');
    }
  };

  const handleDelete = async (entryId: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await wasteService.deleteEntry(entryId);
        await fetchData();
      } catch (err: any) {
        setError(err.message || 'Failed to delete entry');
      }
    }
  };

  // Handle edit - open dialog with entry data
  const handleEdit = (entry: WasteData) => {
    setEditMode(true);
    setEditingEntry(entry);
    // Parse period to get year and month
    const [year, month] = entry.period?.split('-').map(Number) || [new Date().getFullYear(), new Date().getMonth() + 1];
    setFormData({
      category: entry.category,
      wasteType: entry.wasteType,
      disposalMethod: entry.disposalMethod,
      amount: entry.amount.toString(),
      unit: entry.unit,
      facility_id: entry.facility_id || '',
      year: year,
      month: month,
      contractor: entry.contractor || '',
      wasteManifestId: entry.wasteManifestId || '',
      notes: '',
    });
    setEntryDialogOpen(true);
  };

  const handleSubmitEntry = async () => {
    try {
      const entryData = {
        category: formData.category as any,
        waste_type: formData.wasteType as any,
        disposal_method: formData.disposalMethod as any,
        amount: parseFloat(formData.amount) || 0,
        unit: formData.unit as any,
        facility_id: formData.facility_id || undefined,
        year: formData.year,
        month: formData.month,
        contractor: formData.contractor || undefined,
        waste_manifest_id: formData.wasteManifestId || undefined,
        notes: formData.notes || undefined,
      };

      if (editMode && editingEntry) {
        await wasteService.updateEntry(editingEntry.id, entryData);
      } else {
        await wasteService.createEntry(entryData);
      }
      await fetchData();
      setEntryDialogOpen(false);
      setEditMode(false);
      setEditingEntry(null);
      resetFormData();
    } catch (err: any) {
      setError(err.message || 'Failed to save entry');
    }
  };

  const resetFormData = () => {
    setFormData({
      category: '',
      wasteType: '',
      disposalMethod: '',
      amount: '',
      unit: 'kg',
      facility_id: '',
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      contractor: '',
      wasteManifestId: '',
      notes: '',
    });
    setEvidenceFiles([]);
  };

  // Export menu state (Admin only)
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setExportMenuAnchor(null);
    if (filteredData.length === 0) return;

    setExporting(true);
    try {
      const period = selectedPeriod ? `${selectedPeriod.year}` : undefined;
      await wasteService.exportData(format, period);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards - Aligned with Water/Emissions theme */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#f3f4f6', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <DeleteSweep sx={{ color: '#6b7280' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Total Waste
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {(totalWaste / 1000).toFixed(2)}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>tonnes</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#d1fae5', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <Recycling sx={{ color: '#059669' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Recycling Rate
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#059669', lineHeight: 1.2 }}>
                  {recyclingRate.toFixed(0)}%
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>GRI 306-4</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#fee2e2', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <Warning sx={{ color: '#dc2626' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Hazardous Waste
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#dc2626', lineHeight: 1.2 }}>
                  {(totalHazardous / 1000).toFixed(2)}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>tonnes</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5,
                backgroundColor: '#ecfdf5', display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <CheckCircle sx={{ color: '#059669' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Data Status
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
                  {approvedCount}/{totalCount}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', mt: 0.25 }}>
                  {pendingCount > 0 ? `${pendingCount} pending` : 'approved'}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* GRI 306 Metrics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* GRI 306-4: Waste Diverted from Disposal */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', mb: 2 }}>
              GRI 306-4: Waste Diverted from Disposal
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: '#f0fdf4', borderRadius: 2 }}>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#059669' }}>
                    {diversionRate.toFixed(1)}%
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>Diversion Rate</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: '#f0fdf4', borderRadius: 2 }}>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#059669' }}>
                    {((summary?.by_disposal?.['Recycled'] || 0) / 1000).toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>Recycled (t)</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: '#f0fdf4', borderRadius: 2 }}>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#059669' }}>
                    {((summary?.by_disposal?.['Composting'] || 0) / 1000).toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>Composted (t)</Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* GRI 306-5: Waste Directed to Disposal */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827', mb: 2 }}>
              GRI 306-5: Waste Directed to Disposal
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: '#fef2f2', borderRadius: 2 }}>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>
                    {((summary?.by_disposal?.['Landfill'] || 0) / 1000).toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>Landfill (t)</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: '#fef2f2', borderRadius: 2 }}>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>
                    {((summary?.by_disposal?.['Incineration'] || 0) / 1000).toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>Incineration (t)</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: '#fffbeb', borderRadius: 2 }}>
                  <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#d97706' }}>
                    {(((summary?.by_disposal?.['Incineration with Energy Recovery'] || 0)) / 1000).toFixed(2)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>w/ Energy (t)</Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>

      {/* Waste Trend Chart */}
      <Card sx={{ p: 3, mb: 3, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
          Waste Generation Trends
        </Typography>
        <Box sx={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorNonHaz" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6b7280" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6b7280" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorHaz" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#dc2626" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorDiverted" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value} />
              <RechartsTooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload) return null;
                  return (
                    <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: '8px 12px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>{label}</div>
                      {payload.map((p: any) => (
                        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: '12px', marginBottom: '2px' }}>
                          <span style={{ color: p.color }}>{p.dataKey}</span>
                          <span style={{ color: '#374151' }}>{typeof p.value === 'number' ? p.value.toLocaleString() : p.value} kg</span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }} iconSize={12} />
              <Area type="monotone" dataKey="Non-Hazardous" stroke="#6b7280" fillOpacity={1} fill="url(#colorNonHaz)" strokeWidth={2} />
              <Area type="monotone" dataKey="Hazardous" stroke="#dc2626" fillOpacity={1} fill="url(#colorHaz)" strokeWidth={2} />
              <Area type="monotone" dataKey="Diverted" stroke="#059669" fillOpacity={1} fill="url(#colorDiverted)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Card>

      {/* Main Content Card with Tabs */}
      <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
        {/* Tabs & Actions Header */}
        <Box sx={{
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: '#6b7280',
                minHeight: 48,
                px: 2,
              },
              '& .Mui-selected': {
                color: '#111827',
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#6b7280',
                height: 2,
              },
            }}
          >
            <Tab label="Non-Hazardous" />
            <Tab label="Hazardous" icon={<Warning sx={{ fontSize: 16, ml: 0.5 }} />} iconPosition="end" />
          </Tabs>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {permissions.canCreate && (
              <Button
                variant="contained"
                startIcon={<Add sx={{ fontSize: 18 }} />}
                onClick={() => {
                  const category = getCategoryForTab(activeTab);
                  setFormData(prev => ({ ...prev, category }));
                  setEntryDialogOpen(true);
                }}
                sx={{
                  backgroundColor: '#6b7280',
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: 'none',
                  borderRadius: 2,
                  px: 2.5,
                  '&:hover': { backgroundColor: '#4b5563', boxShadow: 'none' },
                }}
              >
                Add Entry
              </Button>
            )}
            {/* Export Button - Admin Only */}
            {permissions.userRole === 'Admin' && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={exporting ? <CircularProgress size={14} /> : <FileDownload sx={{ fontSize: 16 }} />}
                  disabled={filteredData.length === 0 || exporting}
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  sx={{
                    borderColor: '#e5e7eb',
                    color: '#6b7280',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { borderColor: '#d1d5db', backgroundColor: '#f9fafb' },
                    '&:disabled': { borderColor: '#e5e7eb', color: '#9ca3af' },
                  }}
                >
                  {exporting ? 'Exporting...' : 'Export'}
                </Button>
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={Boolean(exportMenuAnchor)}
                  onClose={() => setExportMenuAnchor(null)}
                >
                  <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                  <MenuItem onClick={() => handleExport('xlsx')}>Export as Excel</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>

        {/* Filter Bar */}
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Facility</InputLabel>
            <Select
              value={facilityFilter}
              label="Facility"
              onChange={(e) => {
                setFacilityFilter(e.target.value);
                setPage(0);
              }}
              sx={{ backgroundColor: '#fff', fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.75 } }}
            >
              {facilityOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {facilityFilter !== 'all' && (
            <Button
              size="small"
              onClick={() => {
                setFacilityFilter('all');
                setPage(0);
              }}
              sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}
            >
              Clear Filter
            </Button>
          )}

          <Typography sx={{ color: '#9ca3af', ml: 'auto', fontSize: '0.75rem' }}>
            {filteredData.length} entries
          </Typography>
        </Box>

        {/* Data Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Waste Type</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Disposal</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Facility</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem' }}>Evidence</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#374151', py: 1.25, fontSize: '0.75rem', width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ py: 6, textAlign: 'center' }}>
                    <DeleteSweep sx={{ fontSize: 48, color: '#d1d5db', mb: 2 }} />
                    <Typography variant="body1" sx={{ color: '#6b7280', mb: 0.5 }}>No {getCategoryForTab(activeTab).toLowerCase()} waste data yet</Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>Click "Add Entry" to start tracking waste</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                getCurrentPageData(filteredData).map((row) => {
                  const status = getStatusConfig(row.status);
                  const actions = permissions.getEntryActions(row.status, row.uploadedBy);

                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        '&:hover': { backgroundColor: '#f9fafb' },
                        '& td': { py: 2, borderBottom: '1px solid #f3f4f6' }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {row.hazardous && <Warning sx={{ fontSize: 16, color: '#dc2626' }} />}
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                            {row.wasteType}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.disposalMethod}
                          size="small"
                          sx={{
                            backgroundColor: ['Recycled', 'Composting', 'Reuse'].includes(row.disposalMethod) ? '#d1fae5' : row.disposalMethod === 'Landfill' ? '#fee2e2' : '#f3f4f6',
                            color: ['Recycled', 'Composting', 'Reuse'].includes(row.disposalMethod) ? '#059669' : row.disposalMethod === 'Landfill' ? '#dc2626' : '#4b5563',
                            fontWeight: 500,
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                          {row.amount.toLocaleString()} {row.unit}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                          {row.facility || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                          {row.period ? (() => {
                            const [year, month] = row.period.split('-');
                            return month && year
                              ? `${new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'short' })} ${year}`
                              : row.period;
                          })() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.8rem' }}>
                          {row.uploadedBy ? row.uploadedBy.split('@')[0].split('.').map((n: string) => n.charAt(0).toUpperCase() + n.slice(1)).join(' ') : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={status.icon}
                          label={status.label}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 24,
                            backgroundColor: status.bg,
                            color: status.color,
                            fontWeight: 500,
                            '& .MuiChip-icon': { color: status.color },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {row.attachmentCount && row.attachmentCount > 0 ? (
                          <Tooltip title={`${row.attachmentCount} file${row.attachmentCount !== 1 ? 's' : ''} attached`}>
                            <Chip
                              icon={<AttachFile sx={{ fontSize: 14 }} />}
                              label={row.attachmentCount}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: 22,
                                backgroundColor: '#f3f4f6',
                                color: '#374151',
                                fontWeight: 500,
                                '& .MuiChip-icon': { color: '#6b7280' },
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {/* Edit button - Admin only */}
                          {permissions.userRole === 'Admin' && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEdit(row)} sx={{ color: '#2563eb', '&:hover': { backgroundColor: '#dbeafe' } }}>
                                <Edit sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {actions.canApprove && (
                            <Tooltip title="Approve">
                              <IconButton size="small" onClick={() => { setSelectedEntry(row); setApprovalDialogOpen(true); }} sx={{ color: '#059669', '&:hover': { backgroundColor: '#d1fae5' } }}>
                                <ThumbUp sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {actions.canDelete && (
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => handleDelete(row.id)} sx={{ color: '#6b7280', '&:hover': { backgroundColor: '#f3f4f6' } }}>
                                <Delete sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!actions.canApprove && !actions.canDelete && permissions.userRole !== 'Admin' && (
                            <Tooltip title="View Only">
                              <IconButton size="small" disabled sx={{ color: '#d1d5db' }}><Visibility sx={{ fontSize: 18 }} /></IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredData.length > 0 && (
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' },
            }}
          />
        )}
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Upload Waste Data</Typography>
          <IconButton onClick={() => setUploadDialogOpen(false)} size="small"><Close /></IconButton>
        </Box>
        <DialogContent>
          <Box {...getRootProps()} sx={{ border: '2px dashed', borderColor: isDragActive ? '#6b7280' : '#d1d5db', borderRadius: 3, p: 5, textAlign: 'center', backgroundColor: isDragActive ? '#f9fafb' : '#fafafa', cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { borderColor: '#6b7280', backgroundColor: '#f9fafb' } }}>
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 56, color: isDragActive ? '#6b7280' : '#9ca3af', mb: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>{isDragActive ? 'Drop your file here' : 'Drag & drop your file here'}</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>or click to browse</Typography>
            <Chip label="Accepts Excel (.xlsx) or CSV files" size="small" sx={{ backgroundColor: '#f3f4f6', color: '#6b7280' }} />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog
        open={entryDialogOpen}
        onClose={() => { setEntryDialogOpen(false); setEditMode(false); setEditingEntry(null); resetFormData(); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', maxHeight: '85vh' } }}
      >
        <Box sx={{
          px: 2, py: 1.5,
          backgroundColor: editMode ? '#fef3c7' : '#f3f4f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#111827', fontSize: '0.95rem' }}>
              {editMode ? 'Edit Waste Entry' : 'Add Waste Entry'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
              GRI 306: Track waste generation and disposal
            </Typography>
          </Box>
          <IconButton onClick={() => { setEntryDialogOpen(false); setEditMode(false); setEditingEntry(null); resetFormData(); }} size="small">
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Facility Selection */}
            <FormControl fullWidth size="small">
              <InputLabel shrink sx={{ fontSize: '0.8rem' }}>Facility</InputLabel>
              <Select
                value={formData.facility_id}
                label="Facility"
                onChange={(e) => setFormData({ ...formData, facility_id: e.target.value })}
                sx={{ fontSize: '0.8rem' }}
                displayEmpty
                notched
                renderValue={(value) => {
                  if (value === '') {
                    return 'Organization-wide';
                  }
                  const facility = facilities.find(f => f.id === value);
                  return facility?.name || value;
                }}
              >
                {/* Organization-wide option */}
                <MenuItem value="" sx={{ fontSize: '0.8rem', py: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ fontSize: 16, color: '#6b7280' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>Organization-wide</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Not facility-specific</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                {facilities.length === 0 ? (
                  <MenuItem disabled value="no-facilities">No facilities available</MenuItem>
                ) : (
                  facilities.map((f) => (
                    <MenuItem key={f.id} value={f.id} sx={{ fontSize: '0.8rem', py: 0.75 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>{f.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{f.type} • {f.country}</Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Reporting Period */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Year</InputLabel>
                <Select
                  value={formData.year}
                  label="Year"
                  onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {[2023, 2024, 2025, 2026].map((year) => (
                    <MenuItem key={year} value={year} sx={{ fontSize: '0.8rem', py: 0.5 }}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Month</InputLabel>
                <Select
                  value={formData.month}
                  label="Month"
                  onChange={(e) => setFormData({ ...formData, month: Number(e.target.value) })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {[
                    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
                    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
                    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
                    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
                  ].map((month) => (
                    <MenuItem key={month.value} value={month.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{month.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Category Selection */}
            <FormControl fullWidth required size="small">
              <InputLabel sx={{ fontSize: '0.8rem' }}>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value, wasteType: '', disposalMethod: '' })}
                sx={{ fontSize: '0.8rem' }}
              >
                {WASTE_CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value} sx={{ fontSize: '0.8rem', py: 0.5 }}>{cat.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Waste Type Selection */}
            {formData.category && (
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Waste Type</InputLabel>
                <Select
                  value={formData.wasteType}
                  label="Waste Type"
                  onChange={(e) => setFormData({ ...formData, wasteType: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {WASTE_TYPES[formData.category as keyof typeof WASTE_TYPES]?.map((type) => (
                    <MenuItem key={type} value={type} sx={{ fontSize: '0.8rem', py: 0.5 }}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Disposal Method Selection */}
            {formData.category && (
              <FormControl fullWidth required size="small">
                <InputLabel sx={{ fontSize: '0.8rem' }}>Disposal Method</InputLabel>
                <Select
                  value={formData.disposalMethod}
                  label="Disposal Method"
                  onChange={(e) => setFormData({ ...formData, disposalMethod: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {DISPOSAL_METHODS[formData.category as keyof typeof DISPOSAL_METHODS]?.map((method) => (
                    <MenuItem key={method} value={method} sx={{ fontSize: '0.8rem', py: 0.5 }}>{method}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Amount */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                label="Amount"
                type="number"
                size="small"
                fullWidth
                required
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                InputProps={{ sx: { fontSize: '0.8rem' } }}
                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                placeholder="Enter quantity"
              />
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>Unit</InputLabel>
                <Select
                  value={formData.unit}
                  label="Unit"
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  <MenuItem value="kg" sx={{ fontSize: '0.8rem', py: 0.5 }}>kg</MenuItem>
                  <MenuItem value="tonnes" sx={{ fontSize: '0.8rem', py: 0.5 }}>tonnes</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Contractor (for recycled/disposed waste) */}
            {formData.disposalMethod && ['Recycled', 'Specialized Treatment', 'Incineration'].includes(formData.disposalMethod) && (
              <TextField
                label="Contractor / Third-Party Handler"
                size="small"
                fullWidth
                value={formData.contractor}
                onChange={(e) => setFormData({ ...formData, contractor: e.target.value })}
                InputProps={{ sx: { fontSize: '0.8rem' } }}
                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                placeholder="e.g., ABC Recycling Co."
                helperText="Name of waste handler or recycler"
              />
            )}

            {/* Waste Manifest ID (for tracking chain of custody) */}
            <TextField
              label="Waste Manifest ID (Optional)"
              size="small"
              fullWidth
              value={formData.wasteManifestId}
              onChange={(e) => setFormData({ ...formData, wasteManifestId: e.target.value })}
              InputProps={{ sx: { fontSize: '0.8rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
              placeholder="e.g., WM-2025-001234"
              helperText="Document tracking number for audit trail"
            />

            {/* Evidence Upload */}
            <Box sx={{ border: '1px dashed #d1d5db', borderRadius: 2, p: 2, backgroundColor: '#fafafa' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 1 }}>
                Evidence / Certificates (Optional)
              </Typography>
              <input
                type="file"
                multiple
                accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.docx"
                onChange={(e) => {
                  if (e.target.files) {
                    setEvidenceFiles(Array.from(e.target.files));
                  }
                }}
                style={{ display: 'none' }}
                id="waste-evidence-upload"
              />
              <label htmlFor="waste-evidence-upload">
                <Button
                  component="span"
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
                  sx={{
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f3f4f6' },
                  }}
                >
                  Upload Files
                </Button>
              </label>
              {evidenceFiles.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {evidenceFiles.map((file, idx) => (
                    <Chip
                      key={idx}
                      label={file.name}
                      size="small"
                      onDelete={() => setEvidenceFiles(files => files.filter((_, i) => i !== idx))}
                      sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem', height: 24 }}
                    />
                  ))}
                </Box>
              )}
              <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 1, fontSize: '0.65rem' }}>
                Upload recycling certificates, waste manifests, or weighbridge tickets
              </Typography>
            </Box>

            {/* Notes */}
            <TextField
              label="Notes (Optional)"
              multiline
              rows={2}
              size="small"
              fullWidth
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              InputProps={{ sx: { fontSize: '0.8rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
              placeholder="Add any relevant notes..."
            />
          </Box>
        </DialogContent>

        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
          <Button size="small" onClick={() => { setEntryDialogOpen(false); setEditMode(false); setEditingEntry(null); resetFormData(); }} sx={{ color: '#6b7280', textTransform: 'none', fontSize: '0.8rem' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmitEntry}
            disabled={!formData.category || !formData.wasteType || !formData.disposalMethod || !formData.amount}
            sx={{
              backgroundColor: editMode ? '#f59e0b' : '#10b981',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              boxShadow: 'none',
              '&:hover': { backgroundColor: editMode ? '#d97706' : '#059669', boxShadow: 'none' },
              '&:disabled': { backgroundColor: '#e5e7eb' },
            }}
          >
            {editMode ? 'Update Entry' : 'Submit for Approval'}
          </Button>
        </Box>
      </Dialog>

      {/* Approval Dialog */}
      {selectedEntry && (
        <ApprovalDialog
          open={approvalDialogOpen}
          onClose={() => { setApprovalDialogOpen(false); setSelectedEntry(null); }}
          onApprove={(comment) => { handleApprove(selectedEntry.id, comment); setApprovalDialogOpen(false); setSelectedEntry(null); }}
          onReject={(reason) => { handleReject(selectedEntry.id, reason); setApprovalDialogOpen(false); setSelectedEntry(null); }}
          entryData={{ category: selectedEntry.wasteType, value: selectedEntry.amount, unit: selectedEntry.unit, uploadedBy: selectedEntry.uploadedBy, uploadedAt: selectedEntry.uploadedAt }}
        />
      )}
    </Box>
  );
};
